/**
 * Presentational mobile Share full-screen sheet (PRD-6.1 / SE-8313) — the
 * mobile sibling of the desktop ShareWatchlistModal. Pure JSX; all Apollo and
 * grant writes live in the shared useShareWatchlist hook consumed by the
 * Container.
 *
 * Composes the shared MobileSheetChrome primitive (SE-5081), which owns the
 * surface, the close chip and the dismissal paths. This view supplies:
 *  - Body: the invite row (search, `Can edit` / `Can view`, Invite) above the
 *    WHO HAS ACCESS list with in-place role change and revoke per sharee.
 *  - Sticky footer: Done.
 * @module features/scouting/components/MobileShareWatchlistSheet/MobileShareWatchlistSheetView
 */

import { memo, type ReactNode } from "react";

import ShareAccessRow from "@/features/scouting/components/molecules/ShareAccessRow";
import ShareGroupAccessRow from "@/features/scouting/components/molecules/ShareGroupAccessRow";
import ShareInviteRow from "@/features/scouting/components/molecules/ShareInviteRow";

import MobileSheetChrome from "@/components/molecules/MobileSheetChrome";
import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import { SHARE_ACCESS_STATE_COPY } from "@/features/scouting/utils/shareWatchlistGrants";
import type {
  ShareGroupAccessRowItem,
  ShareGroupCandidate,
} from "@/features/scouting/utils/shareGroupGrants";
import type {
  ShareAccessRowItem,
  ShareAccessState,
  ShareCandidateItem,
  ShareGrantRole,
} from "@/features/scouting/utils/shareWatchlistGrants";

import {
  TEST_ID_SCENARIO_SHARE,
  TEST_ID_MOBILE_SHARE_SHEET,
  TEST_ID_WATCHLIST_SHARE,
} from "e2e/testId";

/** No group line: lists that predate SE-8359 or a closed sheet. */
const NO_GROUP_ROWS: readonly ShareGroupAccessRowItem[] = [];
/**
 * Inert group handlers for a caller that wires no group arm.
 * @returns Nothing.
 */
function NOOP_GROUP_ROLE(): undefined {
  return undefined;
}
/**
 * Inert group revoke for a caller that wires no group arm.
 * @returns Nothing.
 */
function NOOP_GROUP_REVOKE(): undefined {
  return undefined;
}

/** Props for {@link MobileShareWatchlistSheetView}. */
export interface MobileShareWatchlistSheetViewProps {
  /** Whether the sheet is open. */
  readonly isOpen: boolean;
  /** List name for the sheet title ("Share <List>"). */
  readonly listName: string;
  /** WHO HAS ACCESS rows, owner first. */
  readonly accessRows: readonly ShareAccessRowItem[];
  /** Whether the rows are known yet; the empty state renders only when "ready". */
  readonly accessState?: ShareAccessState;
  /** Whether anyone besides the owner has access. */
  readonly hasSharees: boolean;
  /** Current invite search text. */
  readonly searchQuery: string;
  /** Suggestions for the current search. */
  readonly candidates: readonly ShareCandidateItem[];
  /** The picked suggestion, if any. */
  readonly selectedCandidate: ShareCandidateItem | null;
  /** Role the next invite grants. */
  readonly inviteRole: ShareGrantRole;
  /** Inline invite error, or null. */
  readonly inviteError: string | null;
  /** Whether an invite is in flight. */
  readonly isInviting: boolean;
  /** Search change handler. */
  readonly onSearchChange: (next: string) => void;
  /** Suggestion pick handler. */
  readonly onSelectCandidate: (userId: string) => void;
  /** Clears the picked suggestion. */
  readonly onClearCandidate: () => void;
  /** Invite role change handler. */
  readonly onInviteRoleChange: (value: string) => void;
  /** Invite press handler. */
  readonly onInvite: () => void;
  /** Role change handler keyed by grant id. */
  readonly onChangeRole: (userWatchlistId: string, value: string) => void;
  /** Revoke handler keyed by grant id. */
  readonly onRevoke: (userWatchlistId: string) => void;
  /** "Everyone at {organization}" while the owner may still invite it (SE-8359). */
  readonly groupCandidate?: ShareGroupCandidate | null;
  /** The picked group, if any. */
  readonly selectedGroup?: ShareGroupCandidate | null;
  /** Picks the group candidate. */
  readonly onSelectGroup?: () => void;
  /** WHO HAS ACCESS group lines, rendered above the people (SE-8359). */
  readonly groupRows?: readonly ShareGroupAccessRowItem[];
  /** Group role change handler keyed by group grant id. */
  readonly onChangeGroupRole?: (groupGrantId: string, value: string) => void;
  /** Group revoke handler keyed by group grant id. */
  readonly onRevokeGroup?: (groupGrantId: string) => void;
  /** Caption under the invite row (e.g. only Tier 3 matched), or null. */
  readonly inviteCaption?: string | null;
  /**
   * Entity-specific block rendered under WHO HAS ACCESS — the scenario
   * dialog's WHAT THEY CAN SEE section (SE-8356). Lists pass nothing.
   */
  readonly accessSection?: ReactNode;
  /** Done / dismiss (wired to the chrome's close paths too). */
  readonly onClose: () => void;
}

/**
 * Renders the mobile Share full-screen sheet.
 * @param root0 - View props
 * @param root0.isOpen - Whether the sheet is open
 * @param root0.listName - List name for the sheet title
 * @param root0.accessRows - WHO HAS ACCESS rows
 * @param root0.accessState - Whether the rows are known yet
 * @param root0.hasSharees - Whether anyone besides the owner has access
 * @param root0.searchQuery - Current invite search text
 * @param root0.candidates - Suggestions for the current search
 * @param root0.selectedCandidate - The picked suggestion
 * @param root0.inviteRole - Role the next invite grants
 * @param root0.inviteError - Inline invite error
 * @param root0.isInviting - Whether an invite is in flight
 * @param root0.onSearchChange - Search change handler
 * @param root0.onSelectCandidate - Suggestion pick handler
 * @param root0.onClearCandidate - Clears the picked suggestion
 * @param root0.onInviteRoleChange - Invite role change handler
 * @param root0.onInvite - Invite press handler
 * @param root0.onChangeRole - Role change handler keyed by grant id
 * @param root0.onRevoke - Revoke handler keyed by grant id
 * @param root0.groupCandidate - The group candidate, or null
 * @param root0.selectedGroup - The picked group, or null
 * @param root0.onSelectGroup - Picks the group candidate
 * @param root0.groupRows - WHO HAS ACCESS group lines
 * @param root0.onChangeGroupRole - Group role change handler
 * @param root0.onRevokeGroup - Group revoke handler
 * @param root0.inviteCaption - Caption under the invite row
 * @param root0.accessSection - Entity-specific block under WHO HAS ACCESS
 * @param root0.onClose - Done / dismiss
 * @returns The rendered sheet.
 */
const MobileShareWatchlistSheetView = ({
  isOpen,
  listName,
  accessRows,
  accessState = "ready",
  hasSharees,
  searchQuery,
  candidates,
  selectedCandidate,
  inviteRole,
  inviteError,
  isInviting,
  onSearchChange,
  onSelectCandidate,
  onClearCandidate,
  onInviteRoleChange,
  onInvite,
  onChangeRole,
  onRevoke,
  groupCandidate = null,
  selectedGroup = null,
  onSelectGroup,
  groupRows = NO_GROUP_ROWS,
  onChangeGroupRole = NOOP_GROUP_ROLE,
  onRevokeGroup = NOOP_GROUP_REVOKE,
  inviteCaption = null,
  accessSection = null,
  onClose,
}: MobileShareWatchlistSheetViewProps) => (
  <MobileSheetChrome
    isOpen={isOpen}
    onClose={onClose}
    showHandle={false}
    title={`Share ${listName}`}
    footer={
      <Button
        testID={TEST_ID_WATCHLIST_SHARE.DONE_BUTTON}
        tone="primary"
        size="lg"
        label="Done"
        onPress={onClose}
        className="w-full"
      />
    }
  >
    <Stack className="gap-4">
      <ShareInviteRow
        searchQuery={searchQuery}
        candidates={candidates}
        selectedCandidate={selectedCandidate}
        inviteRole={inviteRole}
        inviteError={inviteError}
        isInviting={isInviting}
        onSearchChange={onSearchChange}
        onSelectCandidate={onSelectCandidate}
        onClearCandidate={onClearCandidate}
        onInviteRoleChange={onInviteRoleChange}
        onInvite={onInvite}
        groupCandidate={groupCandidate}
        selectedGroup={selectedGroup}
        onSelectGroup={onSelectGroup}
      />
      {inviteCaption == null ? null : (
        <Box testID={TEST_ID_SCENARIO_SHARE.INVITE_CAPTION}>
          <Text variant="caption" className="text-content-muted">
            {inviteCaption}
          </Text>
        </Box>
      )}

      <Box testID={TEST_ID_MOBILE_SHARE_SHEET.USER_LIST}>
        <Text
          variant="micro-strong"
          className="pb-1 uppercase text-content-muted"
        >
          Who has access
        </Text>
        <Stack testID={TEST_ID_WATCHLIST_SHARE.ACCESS_LIST}>
          {groupRows.map(row => (
            <ShareGroupAccessRow
              key={row.groupGrantId}
              row={row}
              onChangeRole={onChangeGroupRole}
              onRevoke={onRevokeGroup}
            />
          ))}
          {accessRows.map(row => (
            <ShareAccessRow
              key={row.userWatchlistId}
              row={row}
              onChangeRole={onChangeRole}
              onRevoke={onRevoke}
            />
          ))}
        </Stack>
        {accessState === "ready" ? null : (
          <Box testID={TEST_ID_WATCHLIST_SHARE.ACCESS_STATE} className="py-2">
            <Text variant="caption" className="text-content-muted">
              {SHARE_ACCESS_STATE_COPY[accessState]}
            </Text>
          </Box>
        )}
        {accessState === "ready" && !hasSharees ? (
          <Box testID={TEST_ID_WATCHLIST_SHARE.ACCESS_EMPTY} className="py-2">
            <Text variant="caption" className="text-content-muted">
              Private until you invite someone.
            </Text>
          </Box>
        ) : null}
      </Box>
      {accessSection}
    </Stack>
  </MobileSheetChrome>
);

MobileShareWatchlistSheetView.displayName = "MobileShareWatchlistSheetView";

export default memo(MobileShareWatchlistSheetView);
